import { types, flow } from 'mobx-state-tree';
import getReservationQ from './getReservationQ';
import _ from 'lodash';
import getThreadItemsQ from './getThreadItemsQ';
import readMessageM from './readMessageM';
import sendMessageQFn from './sendMessageQFn';
import moment from 'moment';
import ReservationItemModel from './ReservationItemModel';
import {
  query,
  mutate,
} from '../../core/createApolloClient/createApolloClient.client';
import { getp } from '../../helpers/getProp';

const threadItemModel = types
  .model('threadItemModel', {
    id: 0,
    content: types.maybeNull(types.string),
    sentBy: '',
    isRead: true,
    eventName: '',
    createdAt: '',
  })
  .views(self => {
    return {
      get momentOfCreation() {
        return moment.utc(self.createdAt).local();
      },
      get dayOfCreation() {
        return self.momentOfCreation.format('YYYY/MM/DD');
      },
      get dayOfCreationFormatted() {
        const currYear = moment().year();
        if (self.momentOfCreation.year() === currYear) {
          return self.momentOfCreation.format('MMM DD');
        }
        return self.dayOfCreation;
      },
      get timeOfCreation() {
        return self.momentOfCreation.format('hh:mm a');
      },
      get displayTime() {
        const today = moment();
        if (today.isSame(self.momentOfCreation, 'day')) {
          return self.timeOfCreation;
        }
        return self.dayOfCreation;
      },
    };
  });

const InboxItemModel = types
  .model('InboxItemModel', {
    threadId: types.maybeNull(types.identifierNumber),
    reservationId: 0,
    listId: 0,
    hostId: '',
    guestId: '',
    updatedAt: '',
    reservationState: '',
    title: '',
    city: '',
    state: '',
    isRead: false,
    group: '',
    timespan: '',
    coverPhotoName: '',
    isHost: false,
    otherDisplayName: '',
    otherFirstName: '',
    otherPicture: '',
    myPicture: '',
    threadItems: types.array(threadItemModel),
    hasMoreItems: true,
    pollingId: 0,
    currentMessage: '',
    units: types.maybeNull(types.number),
    unitType: types.maybeNull(types.string),
    category: types.maybeNull(types.number),
    category2: types.maybeNull(types.number),
  })
  .actions(self => ({
    getMoreReservationDetails: flow(function*() {
      const res = yield query(getReservationQ, {
        id: self.reservationId,
      });
      const d = getp(res, 'data.getReservation');
      // console.log('d', res);
      if (d) {
        self.total = d.total;
        self.pickupAddress.city.set(d.pickupCity || '');
        self.pickupAddress.state.set(d.pickupState || '');
        self.pickupAddress.street.set(d.pickupStreet || '');
        self.pickupAddress.buildingName.set(d.pickupBuildingName || '');
        self.pickupAddress.zipcode.set(d.pickupZipcode || '');
        self.returnAddress.city.set(d.returnCity || '');
        self.returnAddress.state.set(d.returnState || '');
        self.returnAddress.street.set(d.returnStreet || '');
        self.returnAddress.buildingName.set(d.returnBuildingName || '');
        self.returnAddress.zipcode.set(d.returnZipcode || '');
        self.units = d.units;
        self.unitType = d.unitType;
        self.category = d.listData.category;
        self.category2 = d.listData.category2;
      }
    }),
    addKeys(obj) {
      _.forEach(obj, (v, k) => {
        [];
        if (v !== null && v !== undefined) {
          self[k] = v;
        }
      });
    },
    getNewThreadItems() {
      const vars = {
        threadId: self.threadId,
        newestId: self.newestThreadId,
      };
      self.getThreadItems(vars);
    },
    getOlderThreadItems() {
      const vars = {
        threadId: self.threadId,
        lastId: self.oldestThreadId,
      };
      self.getThreadItems(vars);
    },
    //THREAD ITEM
    getThreadItems: flow(function*(vars) {
      const res = yield query(getThreadItemsQ, vars, {
        fetchPolicy: 'network-only',
      }).catch(err =>
        console.log('ERROR - InboxItemModel.getThreadItems', err),
      );
      const d = getp(res, 'data.getAllThreadItems');
      if (d && d.length > 0) {
        self.threadItems = _.orderBy(
          _.uniqBy([...self.threadItems, ...d], 'id'),
          'id',
        );
        self.isRead = false;
        self.markAsRead();
        if (d.length < 10) {
          self.hasMoreItems = false;
        }
      } else if (d && d.length === 0) {
        self.hasMoreItems = false;
      } else {
        console.log('ERROR - InboxItemModel - cannot get thread Items', res);
      }
    }),
    startMessagePolling: () => {
      self.pollingId = setInterval(self.getNewThreadItems, 6000);
    },
    stopMessagePolling: () => {
      clearInterval(self.pollingId);
      self.pollingId = 0;
    },
    load() {
      const p1 = self.getNewThreadItems();
      const p2 = self.getMoreReservationDetails();
      const p3 = self.markAsRead();
      return Promise.all([p1, p2, p3]).then(res => {
        return res[2];
      });
    },
    // MESSAGES : SEND / READ
    markAsRead: flow(function*() {
      if (self.isRead) return;

      const res = yield mutate(readMessageM, { threadId: self.threadId });

      if (getp(res, 'data.readMessage.status') === 'updated') {
        self.isRead = true;
        return true;
      }
    }),
    onChangeMessageText: text => {
      self.currentMessage = text;
    },
    sendMessage: flow(function*() {
      const vals = {
        threadId: self.threadId,
        content: self.currentMessage,
        eventName: 'message',
      };
      const res = yield sendMessageQFn(vals);
      if (!res.error) {
        self.currentMessage = '';
        self.threadItems.push(res);
      }
    }),
    beforeDestroy() {
      self.stopMessagePolling();
    },
  }))
  .views(self => ({
    get oldestThreadId() {
      if (!self.threadItems || self.threadItems.length === 0) return 0;

      return _.minBy(self.threadItems, 'id').id;
    },
    get newestThreadId() {
      if (!self.threadItems || self.threadItems.length === 0) return 0;

      return _.maxBy(self.threadItems, 'id').id;
    },
    get itemDone() {
      return (
        self.exchangeCompleted ||
        ['declined', 'completed', 'cancelled', 'expired'].indexOf(
          self.reservationState,
        ) >= 0
      );
    },
    get isUnread() {
      if (self.group === 'exchanges') {
        return !self.exchangeCompleted;
      }
      return !self.isRead;
    },
    get otherUserId() {
      return self.isHost ? self.guestId : self.hostId;
    },
    get unreadItemCount() {
      const unreads = self.threadItems.filter(x => !x.isRead);
      console.log('unreads.length', unreads.length);
      return unreads.length;
    },
    get threadItemsOrdered() {
      const items = [];
      let focusedDate = '';
      self.threadItems.forEach(itm => {
        if (itm.dayOfCreation !== focusedDate) {
          focusedDate = itm.dayOfCreation;
          items.push({
            id: 'day-' + focusedDate,
            content: itm.dayOfCreationFormatted,
            sentBy: '',
            isRead: true,
            type: 'day-separator',
            createdAt: itm.dayOfCreation,
          });
        }
        items.push(itm);
      });
      return items;
    },
  }));

export default types.compose(
  'InboxItemMergedModel',
  InboxItemModel,
  ReservationItemModel,
);

/*
  {
       id: 123,
       reservationId: 173,
       listId: 9,
       hostId: 'ff1c8050-1ff4-11e9-98e0-713860d3fa2b',
       guestId: '5f3602a0-296d-11e9-986c-893e86ca52f8',
       updatedAt: 2019-03-04T22:29:38.000Z,
       checkIn: 2019-03-04T00:00:00.000Z,
       checkOut: 2019-03-04T00:00:00.000Z,
       reservationState: 'approved',
       title: 'Bearcat',
       city: 'Lehi',
       state: 'UT',
       isRead: null,
       timezone: 'America/Denver',
       pickupTime: '16:00:00',
       returnTime: '09:00:00',
       pickupDay: 2019-03-03T00:00:00.000Z,
       returnDay: 2019-03-05T00:00:00.000Z,
       ownerPickupTime: 2019-03-04T23:25:39.000Z,
       ownerDropoffTime: 2019-03-04T23:27:14.000Z,
       guestPickupTime: 2019-03-04T23:26:18.000Z,
       guestDropoffTime: 2019-03-04T23:27:19.000Z,
       group: 'approved',
       pickupCompleted: true,
       returnCompleted: true,
       pickupDate: moment.parseZone("2019-03-02T16:00:00.000-07:00"),
       returnDate: moment.parseZone("2019-03-04T09:00:00.000-07:00"),
       timespan: 'Mar 2' },
       */



// WEBPACK FOOTER //
// ./src/mobxStores/Inbox/InboxItemModel.js